import React from 'react';
import styles from '../themeCard.scss';
import { ThumbnailRenderer } from './ThumbnailRenderer';
import classNames from 'classnames';
import { Theme } from '../ThemeCard';

interface ContainerProps {
  selected?: number | null;
  featured?: number | null;
  theme: Theme;
  clickHandler: React.MouseEventHandler<HTMLDivElement>;
  type: 'desktop' | 'mobile';
}

function makeSources(type: ContainerProps['type']) {
  switch (type) {
    case 'desktop':
      return [
        { viewport: 'xs', width: 400 },
        { viewport: 'sm', width: 800 },
        { viewport: 'md', width: 400 },
      ];
    case 'mobile':
      return [
        { viewport: 'xs', width: 100 },
        { viewport: 'sm', width: 200 },
      ];
  }
}

function getMediaApiId(theme: Theme, type: ContainerProps['type']) {
  switch (type) {
    case 'desktop':
      return theme.previewImage?.mediaApiId ?? '';
    case 'mobile':
      return theme.mobilePreviewImage?.mediaApiId ?? '';
  }
}

function makeTitle(type: ContainerProps['type'], theme: Theme) {
  switch (type) {
    case 'desktop':
      return theme.name ?? '';
    case 'mobile':
      return theme.name ? theme.name + ' mobile' : '';
  }
}

export const ThemeCardContainer: React.FC<ContainerProps> = ({ selected, featured, theme, clickHandler, type }) => {
  const className = classNames({
    [styles.themeImage]: type === 'desktop',
    [styles.selected]: type === 'desktop' && selected && !featured,
    [styles.mobileThemeImage]: type === 'mobile',
  });
  const reactKey = String(theme.id) + type === 'mobile' ? '-mobile' : '';
  return (
    <div
      className={className}
      onClick={clickHandler}
      title={makeTitle(type, theme)}
      data-testid={type === 'desktop' ? `thumbnail${selected ? '-selected' : ''}-${theme?.id}` : undefined}
    >
      <ThumbnailRenderer
        mediaApiId={getMediaApiId(theme, type)}
        width={type === 'desktop' ? 400 : 100}
        sources={makeSources(type)}
        slug={`${type}-theme`}
        reactKey={reactKey}
      />
    </div>
  );
};

export const ThemeCardFullDisplayContainer: React.FC<Pick<ContainerProps, 'theme' | 'clickHandler'>> = ({
  theme,
  clickHandler,
}) => {
  return (
    <div className={styles.themeImageFullContainer} title={makeTitle('desktop', theme)}>
      <div className={styles.themeImageFullDesktop} data-testid={`thumbnail-${theme?.id}`} onClick={clickHandler}>
        <ThumbnailRenderer
          reactKey={String(theme.id) + '-desktop'}
          mediaApiId={getMediaApiId(theme, 'desktop')}
          slug="desktop-theme"
          width={211}
          height={158}
          className={styles.themeImageFullImg}
          sources={[
            { viewport: 'xs', width: 211 },
            { viewport: 'sm', width: 211 },
            { viewport: 'md', width: 250 },
          ]}
        />
      </div>
      <div className={styles.themeImageFullMobile}>
        <ThumbnailRenderer
          reactKey={String(theme.id) + '-mobile'}
          mediaApiId={getMediaApiId(theme, 'mobile')}
          slug="mobile-theme"
          width={97}
          height={158}
          className={styles.themeImageFullImg}
          sources={[
            { viewport: 'xs', width: 97 },
            { viewport: 'sm', width: 97 },
            { viewport: 'md', width: 110 },
          ]}
        />
      </div>
    </div>
  );
};
